import React from 'react';
import styled from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrencyStore } from '../store/useCurrencyStore';
import { useFavoritesStore } from '../store/useFavoritesStore';
import ObjectSlider from '../components/ObjectSlider';
import VisualizationGallery from '../components/VisualizationGallery';
import CallRequestModal from '../components/CallRequestModal';
import QuickConsult from '../components/QuickConsult';
import AmenitiesSection from '../components/AmenitiesSection';
import NearbyLocations from '../components/NearbyLocations';
// import FavButton from '../components/FavButton';
import { PublicAPI } from '../api/public';
import { normalizeMediaUrl } from '../api/base';
import UnitsSection from '../components/UnitsSection';
import YMap from '../components/YMap';
import DistrictBadge from '../components/DistrictBadge';

const Wrap = styled.main`padding:16px 0;`;

const Head = styled.div`
  display:grid; gap:14px; grid-template-columns: 1fr;
`;

const RightCol = styled.div`
  display:flex; flex-direction:column; gap:10px; align-self:start;
`;

const ManagerCard = styled.div`
  border:1px solid var(--bdr); border-radius:16px; background:var(--card); box-shadow:var(--shadow);
  padding:12px; display:flex; align-items:center; gap:12px;
  .avatar{ width:52px; height:52px; border-radius:50%; overflow:hidden; background:#e8e8e8; flex:0 0 52px; display:grid; place-items:center; font-weight:800; }
  .name{ font-weight:800; line-height:1.1; }
  .role{ font-size:12px; color:var(--muted); }
  .actions{ margin-left:auto; display:flex; gap:8px; flex-wrap:wrap; }
`;

const OtherManagers = styled.div`
  display:flex; gap:8px; flex-wrap:wrap;
  .chip{
    display:flex; align-items:center; gap:8px; padding:8px 10px;
    border:1px solid var(--bdr); background:var(--card); border-radius:12px;
    img{ width:28px; height:28px; border-radius:50%; object-fit:cover; }
    .nm{ font-size:13px; font-weight:700; }
  }
`;

const Badges = styled.div`
  display:flex; flex-wrap:wrap; gap:6px; margin:6px 0 10px;
  .badge{
    display:inline-flex; align-items:center; gap:6px;
    border:1px solid var(--bdr); background:var(--card);
    border-radius:999px; padding:6px 10px; font-size:12px; line-height:1; white-space:nowrap;
  }
`;

const Section = styled.section`margin:18px 0;`;

const Article = styled.article`
  border:1px solid var(--bdr); border-radius:16px; background:var(--card); box-shadow:var(--shadow);
  padding:14px; line-height:1.55;
  h2,h3{ margin:14px 0 8px; }
  p{ margin:0 0 10px; }
  ul{ margin:0 0 10px 18px; }
  a{ color:var(--primary); text-decoration:underline; }
`;

const GalleryWrap = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  background: var(--card);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  .fav{
    position:absolute; right:14px; top:14px; z-index:3;
    backdrop-filter: blur(2px) saturate(140%);
  }
`;

const PlanBox = styled.div`
  border:1px solid var(--bdr); border-radius:16px; background:var(--card); box-shadow:var(--shadow);
  padding:14px; display:grid; gap:10px;
`;

const PlanRow = styled.div`
  display:grid; grid-template-columns: 120px 1fr; gap:12px; align-items:center;
  .percent{ font-weight:900; font-size:18px; }
  @media(max-width:520px){ grid-template-columns: 90px 1fr; }
`;

const RU_LAT_MAP = { А:'A',Б:'B',В:'V',Г:'G',Д:'D',Е:'E',Ё:'E',Ж:'Zh',З:'Z',И:'I',Й:'Y',К:'K',Л:'L',М:'M',Н:'N',О:'O',П:'P',Р:'R',С:'S',Т:'T',У:'U',Ф:'F',Х:'Kh',Ц:'Ts',Ч:'Ch',Ш:'Sh',Щ:'Shch',Ъ:'',Ы:'Y',Ь:'',Э:'E',Ю:'Yu',Я:'Ya',
  а:'a',б:'b',в:'v',г:'g',д:'g',е:'e',ё:'e',ж:'zh',з:'z',и:'i',й:'y',к:'k',л:'l',м:'m',н:'n',о:'o',п:'p',р:'r',с:'s',т:'t',у:'u',ф:'f',х:'kh',ц:'ts',ч:'ch',ш:'sh',щ:'shch',ъ:'',ы:'y',ь:'',э:'e',ю:'yu',я:'ya' };
const isCyrillic = (s='') => /[А-Яа-яЁё]/.test(s);
const ruToLat = (s='') => s.split('').map(ch => RU_LAT_MAP[ch] ?? ch).join('');
const esc = (v) => String(v ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');

export default function ObjectCard(){
  const { id: idOrSlug, slug } = useParams();
  const key = slug ?? idOrSlug;

  const { i18n, t } = useTranslation();
  const currencyCode = useCurrencyStore(s => s.currency) || 'AED';
  const formatPrice = useCurrencyStore(s => s.formatPrice);
  const formatPriceFromCurrency = useCurrencyStore(s => s.formatPriceFromCurrency);

  // Локальное форматирование с конвертацией валют от базовой валюты объекта
  const formatPriceTail = React.useCallback((value, baseCurrency = 'AED') => {
    if (!value) return i18n.language === 'en' ? 'Price on request' : 
                       i18n.language === 'zh' ? '价格面议' : 'Цена по запросу';
    
    // Используем новую функцию для конвертации от базовой валюты объекта
    return formatPriceFromCurrency(value, baseCurrency);
  }, [formatPriceFromCurrency, i18n.language]);

  const [item, setItem] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [open, setOpen] = React.useState(false);

  const [plansLoading, setPlansLoading] = React.useState(false);
  const [plansError, setPlansError] = React.useState('');
  const [paymentPlans, setPaymentPlans] = React.useState([]);

  const [mgrLoading, setMgrLoading] = React.useState(false);
  const [mgrError, setMgrError] = React.useState('');
  const [managers, setManagers] = React.useState([]);
  const [primaryManager, setPrimaryManager] = React.useState(null);

  const { toggleFavorite, favorites } = useFavoritesStore(s => ({
    toggleFavorite: s.toggleFavorite,
    favorites: s.favorites,
  }));

  React.useEffect(()=>{
    let cancelled = false;
    async function load(){
      setLoading(true); setErr('');
      try{
        const data = await PublicAPI.propertyBySlug(String(key), {
          locale: i18n.language || 'ru',
          currency: 'AED', // Всегда загружаем цены в AED для правильной конвертации
          include: 'media_by_type',
        });
        const prop = data?.property || data || null;
        if (!cancelled) setItem(prop);
      }catch(e){
        if (!cancelled) { setErr(e.message || 'Error'); setItem(null); }
      }finally{
        if (!cancelled) setLoading(false);
      }
    }
    if (key) load();
    return () => { cancelled = true; };
  }, [key, i18n.language, currencyCode]);

  // планы оплаты
  React.useEffect(()=>{
    let cancelled = false;
    async function loadPlans(){
      if (!key) return;
      setPlansLoading(true);
      setPlansError('');
      try{
        const res = await PublicAPI.propertyPaymentPlans(String(key), {
          include_stages: 1,
          locale: i18n.language || 'ru',
        });
        const list = Array.isArray(res?.payment_plans) ? res.payment_plans
                    : Array.isArray(res?.items) ? res.items
                    : [];
        if (!cancelled) setPaymentPlans(list);
      }catch(e){
        if (!cancelled) { setPaymentPlans([]); setPlansError(e?.message || 'Error'); }
      }finally{
        if (!cancelled) setPlansLoading(false);
      }
    }
    loadPlans();
    return ()=>{ cancelled = true; };
  }, [key, i18n.language]);

  // менеджеры
  React.useEffect(()=>{
    let cancelled = false;
    async function loadManagers(){
      if (!key) return;
      setMgrLoading(true); setMgrError('');
      try{
        const res = await PublicAPI.propertyManagers(String(key), {
          locale: i18n.language || 'ru',
        });
        const list = Array.isArray(res?.items) ? res.items : [];
        const primary = res?.primary || res?.primary_manager || list.find(m=>m.is_primary) || null;
        if (!cancelled) { setManagers(list); setPrimaryManager(primary); }
      }catch(e){
        if (!cancelled) { setManagers([]); setPrimaryManager(null); setMgrError(e?.message || 'Error'); }
      }finally{
        if (!cancelled) setMgrLoading(false);
      }
    }
    loadManagers();
    return ()=>{ cancelled = true; };
  }, [key, i18n.language]);

  const favId = React.useMemo(() => String(item?.slug || item?.id || key || ''), [item, key]);
  const favOn = React.useMemo(() => favorites.includes(favId), [favorites, favId]);

  if (loading) {
    return (
      <Wrap className="container">
        <p style={{opacity:.7}}>{t('objectCard.loading')}</p>
      </Wrap>
    );
  }

  if (!item) {
    return (
      <Wrap className="container">
        <h2>{t('objectCard.notFound')}</h2>
        {err && <p style={{color:'#c62828'}}>{err}</p>}
        <Link to="/search" className="btn">← {t('objectCard.backToCatalog')}</Link>
      </Wrap>
    );
  }

  const pickName = (o) => {
    if (!o) return '';
    const lang = i18n.language || 'ru';
    if (lang === 'zh') {
      return o.name_zh || o.name_en || o.name_ru || o.name || o.slug || '';
    }
    if (lang === 'en') {
      const val = o.name_en || o.name || o.name_ru || o.slug || '';
      return isCyrillic(val) ? ruToLat(val) : val;
    }
    return o.name_ru || o.name || o.name_en || o.slug || '';
  };

  const pickTitle = (it) => {
    const lang = i18n.language || 'ru';
    if (lang === 'zh') {
      return it.title_zh || it.title_en || it.title_ru || it.title || it.name || 'Object';
    }
    if (lang === 'en') {
      const val = it.title_en || it.title || it.title_ru || it.name || 'Object';
      return isCyrillic(val) ? ruToLat(val) : val;
    }
    return it.title_ru || it.title || it.title_en || it.name || 'Объект';
  };

  const title        = pickTitle(item);
  const regionName   = pickName(item.region);
  const districtText = pickName(item.district) || '—';
  const city         = item.city_name || item.city || regionName || item.address || '—';

  // КЛИКАБЕЛЬНЫЙ РАЙОН (slug + name)
  const districtSlug = item.district_slug || item.district?.slug || '';
  const districtName = item.district_name || (districtText !== '—' ? districtText : '') || '';

  // Определяем базовую валюту объекта (из админки)
  const objectBaseCurrency = item?.currency?.code || item?.price_currency || 'AED';
  const priceInBaseCurrency = item.price ?? item.price_aed ?? item.priceAED ?? null;
  
  const months      = item.installment_months ?? item.months ?? item.installmentMonths ?? null;
  const handover    = (()=>{
    if (item.handover) return item.handover;
    const q = item.handover_quarter; const y = item.handover_year;
    if (q || y) return `${q ? `Q${q}` : ''}${q && y ? ' ' : ''}${y || ''}`.trim();
    return null;
  })();
  const areaFrom    = item.area_min ? `${item.area_min} ${i18n.language === 'en' ? 'm²' : 'м²'}` : (item.areaFrom || null);

  const dealLabel     = pickName(item.deal_type) || (item.deal_type?.slug || '');
  const propTypeLabel = pickName(item.property_type) || (item.property_type?.slug || '');

  // галерея - только изображения с флагом is_hero_slider
  const gallery = Array.isArray(item.images)
    ? item.images
        .filter(x => x && x.is_hero_slider) // фильтруем только изображения для главного слайдера
        .map(x => normalizeMediaUrl(x?.url || x))
        .filter(Boolean)
    : Array.isArray(item.gallery)
      ? item.gallery.map(normalizeMediaUrl)
      : (item.cover_url || item.cover ? [normalizeMediaUrl(item.cover_url || item.cover)] : []);

  // визуализации — из media_by_type, если сервер вернул; иначе группируем по item.images.viz_type
  let groupsRaw = Array.isArray(item.media_by_type) ? item.media_by_type : [];
  const VIZ_LABELS_RU = { 
    exterior:'Экстерьер', architecture:'Экстерьер', arch:'Экстерьер', outside:'Экстерьер', ext:'Экстерьер',
    interior:'Интерьер', int:'Интерьер', inside:'Интерьер',
    lobby:'Лобби', entrance:'Лобби', hall:'Лобби',
    infrastructure:'Инфраструктура', infra:'Инфраструктура',
    environment:'Окружение', surrounding:'Окружение', area:'Окружение',
    plan:'Планировки', floorplan:'Планировки', layout:'Планировки',
    map:'Карта', view:'Вид', video:'Видео', '360':'360°', other:'Другое' 
  };
  const VIZ_LABELS_EN = { 
    exterior:'Exterior', architecture:'Exterior', arch:'Exterior', outside:'Exterior', ext:'Exterior',
    interior:'Interior', int:'Interior', inside:'Interior',
    lobby:'Lobby', entrance:'Lobby', hall:'Lobby',
    infrastructure:'Infrastructure', infra:'Infrastructure',
    environment:'Environment', surrounding:'Environment', area:'Environment',
    plan:'Plans', floorplan:'Plans', layout:'Plans',
    map:'Map', view:'View', video:'Video', '360':'360°', other:'Other' 
  };
  const VIZ_LABELS = (i18n?.language || 'ru').startsWith('en') ? VIZ_LABELS_EN : VIZ_LABELS_RU;
  if (!groupsRaw.length && Array.isArray(item.images) && item.images.length){
    const byType = new Map();
    for (const m of item.images){
      if (!m) continue;
      const vt = String(m.viz_type || 'other');
      const name = m.viz_type_name || vt;
      const url = normalizeMediaUrl(m?.url || m);
      if (!url) continue;
      if (!byType.has(vt)) byType.set(vt, { viz_type: vt, viz_type_name: name, items: [] });
      byType.get(vt).items.push({ url });
    }
    groupsRaw = Array.from(byType.values());
  }

  const visualGroups = (groupsRaw || []).map(g => {
    const slug = String(g?.viz_type || 'other').toLowerCase();
    const display = VIZ_LABELS[slug] || g?.viz_type_name || g?.viz_type || '—';
    return {
      key: slug,
      name: display,
      images: (g?.items || []).map(m => normalizeMediaUrl(m?.url || m)).filter(Boolean),
    };
  }).filter(g => g.images.length);

  // автоописание (если пусто)
  const amenitiesNames = Array.isArray(item.amenities)
    ? item.amenities
        .map(a => (i18n.language === 'en'
          ? (a?.amenity?.name_en || a?.amenity?.name_ru || a?.name_en || a?.name_ru || a?.slug)
          : (a?.amenity?.name_ru || a?.amenity?.name_en || a?.name_ru || a?.name_en || a?.slug)))
        .filter(Boolean)
    : [];

  const buildAutoDescription = () => {
    const currency = item.currency?.symbol || item.currency?.code || 'AED';
    const price    = item.price ?? item.price_aed ?? null;
    const quarter  = item.handover_quarter ? `Q${item.handover_quarter}` : null; // <-- фикс
    const year     = item.handover_year ? String(item.handover_year) : null;
    const hand     = quarter && year ? `${quarter} ${year}` : (year || null);

    const facts = [
      dealLabel ? `<li><b>${i18n.language === 'en' ? 'Deal type' : 'Тип сделки'}:</b> ${esc(dealLabel)}</li>` : '',
      propTypeLabel ? `<li><b>${i18n.language === 'en' ? 'Property type' : 'Тип объекта'}:</b> ${esc(propTypeLabel)}</li>` : '',
      (()=>{
        const r = item?.bedrooms_range; const b = item?.bedrooms;
        if (r && (r.min!=null || r.max!=null)){
          const a = r.min; const z = r.max;
          const v = (a!=null && z!=null && a!==z) ? `${a}–${z}` : String(a!=null ? a : z);
          return `<li><b>${i18n.language === 'en' ? 'Bedrooms' : 'Спальни'}:</b> ${esc(v)}</li>`;
        }
        return (b!=null) ? `<li><b>${i18n.language === 'en' ? 'Bedrooms' : 'Спальни'}:</b> ${esc(b)}</li>` : '';
      })(),
      (()=>{
        const r = item?.bathrooms_range; const m = item?.bathrooms;
        if (r && (r.min!=null || r.max!=null)){
          const a = r.min; const z = r.max;
          const v = (a!=null && z!=null && a!==z) ? `${a}–${z}` : String(a!=null ? a : z);
          return `<li><b>${i18n.language === 'en' ? 'Bathrooms' : 'Санузлы'}:</b> ${esc(v)}</li>`;
        }
        return (m!=null) ? `<li><b>${i18n.language === 'en' ? 'Bathrooms' : 'Санузлы'}:</b> ${esc(m)}</li>` : '';
      })(),
      (()=>{
        const r = item?.area_total_range; const a = item?.area_total || item?.area_m2;
        if (r && (r.min!=null || r.max!=null)){
          const mn = r.min; const mx = r.max;
          const v = (mn!=null && mx!=null && mn!==mx) ? `${mn}–${mx} m²` : `${mn!=null ? mn : mx} m²`;
          return `<li><b>${i18n.language === 'en' ? 'Area' : 'Площадь'}:</b> ${esc(v)}</li>`;
        }
        return a ? `<li><b>${i18n.language === 'en' ? 'Area' : 'Площадь'}:</b> ${esc(a)} m²</li>` : '';
      })(),
      hand ? `<li><b>Handover:</b> ${esc(hand)}</li>` : '',
      (regionName || districtText) ? `<li><b>${i18n.language === 'en' ? 'Location' : 'Локация'}:</b> ${esc([regionName, districtText !== '—' ? districtText : ''].filter(Boolean).join(' / '))}</li>` : '',
      item.address ? `<li><b>${i18n.language === 'en' ? 'Address' : 'Адрес'}:</b> ${esc(item.address)}</li>` : '',
      price != null || item.price_max != null || true ? `<li><b>${i18n.language === 'en' ? 'Price' : 'Цена'}:</b> ${(() => {
        if ((!price || price === 0) && (!item.price_max || item.price_max === 0)) {
          return i18n.language === 'en' ? 'Price on request' : 
                 i18n.language === 'zh' ? '价格面议' : 'Цена по запросу';
        }
        const priceMax = item.price_max;
        if (price && price > 0 && priceMax && priceMax > 0 && price !== priceMax) {
          return `${formatPriceFromCurrency(price, objectBaseCurrency)} - ${formatPriceFromCurrency(priceMax, objectBaseCurrency)}`;
        }
        return (price && price > 0) ? formatPriceFromCurrency(price, objectBaseCurrency) : 
               (i18n.language === 'en' ? 'Price on request' : 
                i18n.language === 'zh' ? '价格面议' : 'Цена по запросу');
      })()}</li>` : '',
      (item.price_per_m2_min || item.price_per_m2_max || true) ? `<li><b>${i18n.language === 'en' ? 'Price per m²' : 'Цена за м²'}:</b> ${(() => {
        const priceM2Min = item.price_per_m2_min;
        const priceM2Max = item.price_per_m2_max;
        
        // Если обе цены пустые или равны 0, показываем "Цена по запросу"
        if ((!priceM2Min || priceM2Min === 0) && (!priceM2Max || priceM2Max === 0)) {
          return i18n.language === 'en' ? 'Price on request' : 
                 i18n.language === 'zh' ? '价格面议' : 'Цена по запросу';
        }
        
        if (priceM2Min && priceM2Min > 0 && priceM2Max && priceM2Max > 0 && priceM2Min !== priceM2Max) {
          return `${formatPriceFromCurrency(priceM2Min, objectBaseCurrency)} - ${formatPriceFromCurrency(priceM2Max, objectBaseCurrency)}/м²`;
        }
        if (priceM2Min && priceM2Min > 0) {
          return `${formatPriceFromCurrency(priceM2Min, objectBaseCurrency)}/м²`;
        }
        if (priceM2Max && priceM2Max > 0) {
          return `${i18n.language === 'en' ? 'up to' : 'до'} ${formatPriceFromCurrency(priceM2Max, objectBaseCurrency)}/м²`;
        }
        return i18n.language === 'en' ? 'Price on request' : 
               i18n.language === 'zh' ? '价格面议' : 'Цена по запросу';
      })()}</li>` : '',
    ].filter(Boolean).join('');

    const about = [
      propTypeLabel || (i18n.language === 'en' ? 'Property' : 'Объект недвижимости'),
      (regionName || districtText) ? (i18n.language === 'en'
        ? `in ${[districtText !== '—' ? districtText : '', regionName].filter(Boolean).join(', ')}`
        : `в районе ${[districtText !== '—' ? districtText : '', regionName].filter(Boolean).join(', ')}`) : '',
      (()=>{
        const r = item?.bedrooms_range; const b = item?.bedrooms;
        const val = r ? ((r.min!=null && r.max!=null && r.min!==r.max) ? `${r.min}–${r.max}` : (r.min!=null ? r.min : r.max)) : b;
        return (val!=null) ? (i18n.language === 'en' ? `, ${val} br` : `, ${val} спальни`) : '';
      })(),
      (()=>{
        const r = item?.area_total_range; const a = item?.area_total || item?.area_m2;
        const val = r ? ((r.min!=null && r.max!=null && r.min!==r.max) ? `${r.min}–${r.max}` : (r.min!=null ? r.min : r.max)) : a;
        return (val!=null) ? (i18n.language === 'en' ? `, total area ~${val} m²` : `, общая площадь ~${val} м²`) : '';
      })()
    ].filter(Boolean).join(' ');

    const amenitiesHtml = amenitiesNames.length
      ? `<h3>${i18n.language === 'en' ? 'Amenities' : 'Удобства'}</h3><ul>${amenitiesNames.map(a=>`<li>${esc(a)}</li>`).join('')}</ul>`
      : '';

    return `
      <h3>${i18n.language === 'en' ? 'Project description' : 'Описание проекта'}</h3>
      <p>${about}. ${i18n.language === 'en' ? 'Current status' : 'Статус'}: ${esc(item.status || 'active')}.</p>
      <h3>${i18n.language === 'en' ? 'Information about the property' : 'Информация об объекте'}</h3>
      <ul>${facts}</ul>
      ${amenitiesHtml}
    `.trim();
  };

  const descriptionHtmlRaw =
    item.description ||
    (i18n.language === 'en' ? item.description_en : item.description_ru) ||
    '';

  // Пустое описание бывает с "пустыми" HTML-тегами или &nbsp;.
  const isMeaningfullyEmpty = (html='') => {
    const text = String(html)
      .replace(/<[^>]*>/g, '')     // убрать все теги
      .replace(/&nbsp;/g, ' ')     // небуквенные пробелы → обычные
      .replace(/\s+/g, '')        // все пробелы
      .trim();
    return text.length === 0;
  };

  const descriptionHtml = isMeaningfullyEmpty(descriptionHtmlRaw)
    ? buildAutoDescription()
    : descriptionHtmlRaw;

  const initialsFrom = (name = '') => name.trim().split(/\s+/).map(x=>x[0]).slice(0,2).join('').toUpperCase() || 'M';

  const planTitle = (p) => {
    const lang = i18n.language || 'ru';
    if (lang === 'zh') return p?.title_zh || p?.title_en || p?.title_ru || p?.title || 'План оплаты';
    if (lang === 'en') return p?.title_en || p?.title_ru || p?.title || 'Payment Plan';
    return p?.title_ru || p?.title || 'План оплаты';
  };
  
  const planDesc = (p) => {
    const lang = i18n.language || 'ru';
    if (lang === 'zh') return p?.description_zh || p?.description_en || p?.description_ru || '';
    if (lang === 'en') return p?.description_en || p?.description_ru || '';
    return p?.description_ru || '';
  };

  const fullName = (m) =>
    m?.full_name ||
    [m?.first_name, m?.last_name].filter(Boolean).join(' ') ||
    [m?.first_name_ru, m?.last_name_ru].filter(Boolean).join(' ') ||
    [m?.first_name_en, m?.last_name_en].filter(Boolean).join(' ') ||
    [m?.first_name_zh, m?.last_name_zh].filter(Boolean).join(' ') ||
    'Менеджер';

  const pos = (m) => {
    if (m?.position) return m.position;
    // Fallback with language priority: zh → en → ru
    if (i18n.language === 'zh') {
      return m?.position_zh || m?.position_en || m?.position_ru || '房地产专家';
    }
    if (i18n.language === 'en') {
      return m?.position_en || m?.position_ru || m?.position_zh || 'Property Expert';
    }
    return m?.position_ru || m?.position_en || m?.position_zh || 'Эксперт по недвижимости';
  };

  const tgLink = (m) => {
    if (m?.telegram && /^https?:\/\//i.test(m.telegram)) return m.telegram;
    const raw = (m?.telegram || m?.phone || '').replace(/\D/g,'');
    return raw ? `https://t.me/${raw}` : 'https://t.me/';
  };

  const primary = primaryManager;
  const others = managers.filter(m => !primary || m.id !== primary.id);

  // Developer name (from API fields)
  const developerName = (()=>{
    const lang = i18n.language || 'ru';
    const ru = item?.developer_name_ru || item?.developer_name || item?.developer;
    const en = item?.developer_name_en || item?.developer_name || item?.developer;
    const zh = item?.developer_name_zh;
    
    if (lang === 'zh') return zh || en || ru || '';
    if (lang === 'en') return en || ru || '';
    return ru || en || '';
  })();

  // Helpers for ranges presentation
  const bedroomsLabel = (()=>{
    const r = item?.bedrooms_range;
    if (r && (r.min!=null || r.max!=null)){
      const a=r.min, z=r.max; if (a!=null && z!=null && a!==z) return `${a}–${z}`;
      return String(a!=null ? a : z);
    }
    if (item?.bedrooms!=null) return String(item.bedrooms);
    return null;
  })();
  const areaLabel = (()=>{
    const r = item?.area_total_range;
    if (r && (r.min!=null || r.max!=null)){
      const a=r.min, z=r.max; if (a!=null && z!=null && a!==z) return `${a}–${z} m²`;
      return `${a!=null ? a : z} m²`;
    }
    if (item?.area_total || item?.area_m2) return `${item.area_total || item.area_m2} m²`;
    return null;
  })();
  const bathroomsLabel = (()=>{
    const r = item?.bathrooms_range;
    if (r && (r.min!=null || r.max!=null)){
      const a=r.min, z=r.max; if (a!=null && z!=null && a!==z) return `${a}–${z}`;
      return String(a!=null ? a : z);
    }
    if (item?.bathrooms!=null) return String(item.bathrooms);
    return null;
  })();
  const brRange = item?.bedrooms_range || null;
  const brMin = brRange?.min; const brMax = brRange?.max;
  const arRange = item?.area_total_range || null;
  const arMin = arRange?.min; const arMax = arRange?.max;

  const SummaryCard = styled.div`
    border:1px solid var(--bdr); border-radius:16px; background:var(--card); box-shadow:var(--shadow);
    padding:14px; display:grid; gap:8px; align-self:start;
    .title{ font-weight:800; }
    .muted{ color:var(--muted); font-size:13px; }
    .label{ text-transform:uppercase; letter-spacing:.06em; font-size:11px; color:var(--muted); }
    .price{ 
      font-weight: 900 !important; 
      font-size: 24px !important; 
      line-height: 1.2 !important;
      color: inherit !important;
    }
    /* Дополнительная специфичность для цен */
    div.price {
      font-weight: 900 !important; 
      font-size: 24px !important; 
      line-height: 1.2 !important;
    }
    .row{ display:flex; gap:10px; align-items:center; }
    .iconBtn{ width:36px; height:36px; border-radius:999px; border:1px solid var(--bdr); background:var(--card); display:grid; place-items:center; color:var(--muted); }
    .iconBtn svg{ width:20px; height:20px; display:block; }
  `;

  const FavCircle = styled.button`
    width:36px; height:36px; border-radius:999px; border:1px solid var(--bdr);
    background:var(--card); display:grid; place-items:center; color:var(--muted);
    transition: color .2s ease, border-color .2s ease, transform .08s ease;
    svg{ width:20px; height:20px; display:block; }
    &[data-on="true"]{ color:#ef4444; border-color:#ef4444; }
    &:hover{ transform: translateY(-1px); }
    &:active{ transform: scale(.96); }
  `;

  function SmallHeartIcon(){
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
        <path d="M12.1 21.35c-.1 0-.2 0-.3-.1C6.14 17.24 3 13.98 3 10.25 3 7.35 5.35 5 8.25 5c1.71 0 3.32.8 4.35 2.07A5.56 5.56 0 0 1 16.95 5C19.85 5 22.2 7.35 22.2 10.25c0 3.73-3.14 6.99-8.8 11.01-.1.07-.2.09-.3.09z"/>
      </svg>
    );
  }

  function PinIcon(){
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
        <path d="M12 22c-.3 0-.6-.15-.78-.42C9.26 18.67 6 14.18 6 10a6 6 0 1 1 12 0c0 4.18-3.26 8.67-5.22 11.58-.18.27-.48.42-.78.42ZM12 6a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z"/>
      </svg>
    );
  }

  return (
    <Wrap className="container">
      <Head>
        <GalleryWrap>
          {gallery.length > 0 ? (
            <ObjectSlider 
              property={item}
              images={gallery}
              formatPrice={(property) => {
                const price = property.price || property.price_base;
                if (!price || price === 0) {
                  return i18n.language === 'en' ? 'Price on request' : 
                         i18n.language === 'zh' ? '价格面议' : 'Цена по запросу';
                }
                return formatPriceFromCurrency(price, property?.currency?.code || property?.price_currency || 'AED');
              }}
            />
          ) : (
            <div style={{
              aspectRatio: '16/9',
              display: 'grid',
              placeItems: 'center',
              background: '#f5f5f5',
              color: '#666'
            }}>
              {i18n.language === 'en' ? 'No images' : 'Нет изображений'}
            </div>
          )}
        </GalleryWrap>
      </Head>

      {/* Сноска под слайдером (типы/распродажа/сроки + застройщик) */}
      <Section>
        <div style={{display:'grid', gridTemplateColumns:'2fr 1fr', gap:14}}>
          <Article>
            <div className="muted" style={{margin:'0 0 6px'}}>{propTypeLabel || (i18n.language==='en' ? 'Property' : 'Объект')}</div>
            <h2 style={{margin:'0 0 6px'}}>{title}</h2>
            <div className="muted" style={{margin:'0 0 8px'}}>
              {city}
              {districtSlug && districtName ? (
                <>
                  {' • '}
                  <Link to={`/districts/${encodeURIComponent(districtSlug)}`} style={{ textDecoration: 'underline' }}>
                    {districtName}
                  </Link>
                </>
              ) : (districtText && districtText !== '—' ? ` • ${districtText}` : null)}
            </div>
            {!!developerName && (
              <div style={{margin:'0 0 10px'}}>
                <b>{t('objectCard.developer')}:</b> {developerName}
              </div>
            )}
            <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
              {(item.status==='active' || item.is_active) && (
                <span className="badge" style={{border:'1px solid var(--bdr)', borderRadius:10, padding:'6px 10px', background:'var(--card)'}}>{t('objectCard.onSale')}</span>
              )}
              {!!handover && (
                <span className="badge" style={{border:'1px solid var(--bdr)', borderRadius:10, padding:'6px 10px', background:'var(--card)'}}>{handover}</span>
              )}
              {item.installment_available && (
                <span className="badge" style={{border:'1px solid var(--bdr)', borderRadius:10, padding:'6px 10px', background:'var(--card)'}}>{t('objectCard.installments')}</span>
              )}
              {item.is_investment && (
                <span className="badge" style={{border:'1px solid var(--bdr)', borderRadius:10, padding:'6px 10px', background:'var(--card)'}}>{t('objectCard.investment')}</span>
              )}
              {item.is_featured && (
                <span className="badge" style={{border:'1px solid var(--bdr)', borderRadius:10, padding:'6px 10px', background:'var(--card)'}}>{t('objectCard.featured')}</span>
              )}
            </div>

            {/* Текстовые строки под сроком сдачи */}
            {(brMin!=null || brMax!=null) && (
              <div style={{marginTop:8}}>
                {brMin!=null && brMax!=null && brMin!==brMax
                  ? `${t('objectCard.bedroomsFrom')} ${brMin} ${t('objectCard.bedroomsTo')} ${brMax}`
                  : `${t('objectCard.bedrooms')}: ${brMin!=null ? brMin : brMax}`}
              </div>
            )}
            {(arMin!=null || arMax!=null) && (
              <div>
                {arMin!=null && arMax!=null && arMin!==arMax
                  ? `${t('objectCard.areaFrom')} ${arMin} ${t('objectCard.areaTo')} ${arMax} m²`
                  : `${t('objectCard.area')}: ${arMin!=null ? arMin : arMax} m²`}
              </div>
            )}
          </Article>

          <div>
            {/* Сводная карточка над менеджером */}
            <SummaryCard>
              <div className="title">{title}</div>
              <div className="muted">{districtText !== '—' ? `${districtText}, ` : ''}{regionName}</div>
              <div className="label">{t('objectCard.priceFrom')}</div>
              <div className="row" style={{justifyContent:'space-between'}}>
                <div 
                  className="price"
                  style={{fontSize: '24px', fontWeight: '900'}}
                  dangerouslySetInnerHTML={{
                    __html: (() => {
                      const priceMin = priceInBaseCurrency;
                      const priceMax = item.price_max;
                      
                      if ((!priceMin || priceMin === 0) && (!priceMax || priceMax === 0)) {
                        return i18n.language === 'en' ? 'Price on request' : 
                               i18n.language === 'zh' ? '价格面议' : 'Цена по запросу';
                      }
                      
                      if (priceMin && priceMin > 0 && priceMax && priceMax > 0 && priceMin !== priceMax) {
                        return `${formatPriceFromCurrency(priceMin, objectBaseCurrency)} - ${formatPriceFromCurrency(priceMax, objectBaseCurrency)}`;
                      }
                      return (priceMin && priceMin > 0) ? formatPriceFromCurrency(priceMin, objectBaseCurrency) : 
                             (i18n.language === 'en' ? 'Price on request' : 
                              i18n.language === 'zh' ? '价格面议' : 'Цена по запросу');
                    })()
                  }}
                />
                <div className="row">
                  <a className="iconBtn" href="#location" title={t('objectCard.location')}>
                    <PinIcon />
                  </a>
                  <FavCircle
                    type="button"
                    data-on={favOn ? 'true' : 'false'}
                    aria-pressed={favOn ? 'true' : 'false'}
                    title={favOn ? t('objectCard.inFavorites') : t('objectCard.addToFavorites')}
                    onClick={()=>toggleFavorite(favId)}
                    onMouseDown={(e)=>e.preventDefault()}
                  >
                    <SmallHeartIcon />
                  </FavCircle>
                </div>
              </div>
              
              {/* Цена за кв.м */}
              {(item.price_per_m2_min || item.price_per_m2_max || true) && (
                <>
                  <div className="label" style={{marginTop: '8px'}}>
                    {t('objectCard.pricePerM2')}
                  </div>
                  <div 
                    className="price"
                    style={{fontSize: '24px', fontWeight: '900'}}
                    dangerouslySetInnerHTML={{
                      __html: (() => {
                        const priceM2Min = item.price_per_m2_min;
                        const priceM2Max = item.price_per_m2_max;
                        
                        // Если обе цены пустые или равны 0, показываем "Цена по запросу"
                        if ((!priceM2Min || priceM2Min === 0) && (!priceM2Max || priceM2Max === 0)) {
                          return i18n.language === 'en' ? 'Price on request' : 
                                 i18n.language === 'zh' ? '价格面议' : 'Цена по запросу';
                        }
                        
                        if (priceM2Min && priceM2Min > 0 && priceM2Max && priceM2Max > 0 && priceM2Min !== priceM2Max) {
                          return `${formatPriceFromCurrency(priceM2Min, objectBaseCurrency)} - ${formatPriceFromCurrency(priceM2Max, objectBaseCurrency)}/м²`;
                        }
                        if (priceM2Min && priceM2Min > 0) {
                          return `${formatPriceFromCurrency(priceM2Min, objectBaseCurrency)}/м²`;
                        }
                        if (priceM2Max && priceM2Max > 0) {
                          return `${t('objectCard.upTo')} ${formatPriceFromCurrency(priceM2Max, objectBaseCurrency)}/м²`;
                        }
                        return i18n.language === 'en' ? 'Price on request' : 
                               i18n.language === 'zh' ? '价格面议' : 'Цена по запросу';
                      })()
                    }}
                  />
                </>
              )}
            </SummaryCard>

            {mgrLoading && <div className="muted" style={{margin:'6px 0'}}>{t('objectCard.loadingManagers')}</div>}
            {!!mgrError && !primary && !others.length && (
              <div style={{color:'#c62828', margin:'6px 0'}}>{mgrError}</div>
            )}

            {primary ? (
              <ManagerCard>
                <div className="avatar">
                  {(primary.avatar_thumb || primary.avatar) ? (
                    <img
                      src={normalizeMediaUrl(primary.avatar_thumb || primary.avatar)}
                      alt={fullName(primary)}
                      style={{width:'100%',height:'100%',objectFit:'cover'}}
                    />
                  ) : (
                    <span>{initialsFrom(fullName(primary))}</span>
                  )}
                </div>
                <div>
                  <div className="name">{fullName(primary)}</div>
                  <div className="role">{pos(primary)}</div>
                  {(primary.phone || primary.email) && (
                    <div className="role" style={{marginTop:4}}>
                      {primary.phone || ''}{primary.email ? ` • ${primary.email}` : ''}
                    </div>
                  )}
                </div>
                <div className="actions">
                  <a className="btn" href={tgLink(primary)} target="_blank" rel="noreferrer">{t('objectCard.telegram')}</a>
                  <button className="btn" onClick={()=>setOpen(true)}>{t('objectCard.requestCall')}</button>
                </div>
              </ManagerCard>
            ) : (
              <ManagerCard>
                <div className="avatar"><span>{initialsFrom('M')}</span></div>
                <div>
                  <div className="name">{item.agent_name || 'Евгений'}</div>
                  <div className="role">{item.agent?.title || t('objectCard.propertyExpert')}</div>
                  <div className="role" style={{marginTop:4}}>{item.phone || '+971 52 342-36-86'}</div>
                </div>
                <div className="actions">
                  <a className="btn" href={item.telegram || 'https://t.me/astorius_ae'} target="_blank" rel="noreferrer">{t('objectCard.telegram')}</a>
                  <button className="btn" onClick={()=>setOpen(true)}>{t('objectCard.requestCall')}</button>
                </div>
              </ManagerCard>
            )}

            {!!others.length && (
              <OtherManagers style={{marginTop:8}}>
                {others.map(m => (
                  <div className="chip" key={m.id}>
                    {(m.avatar_thumb || m.avatar) && (
                      <img
                        src={normalizeMediaUrl(m.avatar_thumb || m.avatar)}
                        alt={fullName(m)}
                      />
                    )}
                    <span className="nm">{fullName(m)}</span>
                  </div>
                ))}
              </OtherManagers>
            )}
          </div>
        </div>
      </Section>

      {/* Планировки */}
      <UnitsSection 
        slugOrId={item.slug || key} 
        propertyType={item.property_type?.slug || item.property_type} 
      />

      {/* Условия оплаты - показываем только если есть планы */}
      {(!!paymentPlans.length || plansLoading || (plansError && !paymentPlans.length)) && (
        <Section>
          <h2 style={{margin:'0 0 10px'}}>{t('objectCard.paymentPlans')}</h2>

          {plansLoading && <div style={{opacity:.7}}>{t('objectCard.loading')}</div>}
          {!!plansError && !paymentPlans.length && <div style={{color:'#c62828', marginBottom:10}}>{plansError}</div>}

          {!!paymentPlans.length && (
            <div style={{display:'grid', gap:12}}>
              {paymentPlans.map((plan) => (
                <Article key={String(plan.id || plan.title || Math.random())}>
                  <h3 style={{margin:'0 0 6px'}}>{planTitle(plan)}</h3>
                  {!!planDesc(plan) && (
                    <div className="muted" style={{margin:'0 0 10px'}}>
                      {planDesc(plan)}
                    </div>
                  )}

                  {Array.isArray(plan.stages) && plan.stages.length > 0 ? (
                    <PlanBox>
                      {plan.stages.map((s)=>(
                        <PlanRow key={String(s.id || (s.label ?? '') + (s.percent ?? ''))}>
                          <div className="percent">{String(s.percent ?? '').toString().replace(/%?$/,'%')}</div>
                          <div className="desc">{s.label || t('objectCard.stage')}</div>
                        </PlanRow>
                      ))}
                    </PlanBox>
                  ) : (
                    <div style={{opacity:.75}}>
                      {t('objectCard.noStages')}
                    </div>
                  )}
                </Article>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Описание */}
      {descriptionHtml && (
        <Section>
          <h2 style={{margin:'0 0 10px'}}>
            {t('objectCard.description')}
          </h2>
          <Article dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
        </Section>
      )}

      {/* Слайдер визуализаций после описания */}
      {visualGroups.length ? (
        <Section>
          <h2 style={{margin:'0 0 16px'}}>{t('objectCard.visualizations')}</h2>
          <VisualizationGallery groups={visualGroups} />
        </Section>
      ) : null}

      {/* Информация об объекте — всегда показываем */}
      <Section>
        <h2 style={{margin:'0 0 10px'}}>{t('objectCard.propertyInfo')}</h2>
        <Article>
          <ul>
            {!!dealLabel && <li><b>{t('objectCard.dealType')}:</b> {dealLabel}</li>}
            {!!propTypeLabel && <li><b>{t('objectCard.propertyType')}:</b> {propTypeLabel}</li>}
            {bedroomsLabel!=null && <li><b>{t('objectCard.bedrooms')}:</b> {bedroomsLabel}</li>}
            {areaLabel!=null && <li><b>{t('objectCard.area')}:</b> {areaLabel}</li>}
            {(regionName || districtText) && (
              <li><b>{t('objectCard.location')}:</b> {[regionName, (districtText!== '—' ? districtText : '')].filter(Boolean).join(' / ')}</li>
            )}
            {!!item.address && <li><b>{t('objectCard.address')}:</b> {item.address}</li>}
            {priceInBaseCurrency!=null || item.price_max!=null || true ? (
              <li>
                <b>{t('objectCard.price')}:</b> 
                <span dangerouslySetInnerHTML={{
                  __html: (() => {
                    const priceMin = priceInBaseCurrency;
                    const priceMax = item.price_max;
                    
                    if ((!priceMin || priceMin === 0) && (!priceMax || priceMax === 0)) {
                      return i18n.language === 'en' ? 'Price on request' : 
                             i18n.language === 'zh' ? '价格面议' : 'Цена по запросу';
                    }
                    
                    if (priceMin && priceMin > 0 && priceMax && priceMax > 0 && priceMin !== priceMax) {
                      return `${formatPriceFromCurrency(priceMin, objectBaseCurrency)} - ${formatPriceFromCurrency(priceMax, objectBaseCurrency)}`;
                    }
                    return (priceMin && priceMin > 0) ? formatPriceFromCurrency(priceMin, objectBaseCurrency) : 
                           (i18n.language === 'en' ? 'Price on request' : 
                            i18n.language === 'zh' ? '价格面议' : 'Цена по запросу');
                  })()
                }} />
              </li>
            ) : null}
            {(item.price_per_m2_min || item.price_per_m2_max || true) && (
              <li>
                <b>{t('objectCard.pricePerM2')}:</b> 
                <span dangerouslySetInnerHTML={{
                  __html: (() => {
                    const priceM2Min = item.price_per_m2_min;
                    const priceM2Max = item.price_per_m2_max;
                    
                    // Если обе цены пустые или равны 0, показываем "Цена по запросу"
                    if ((!priceM2Min || priceM2Min === 0) && (!priceM2Max || priceM2Max === 0)) {
                      return i18n.language === 'en' ? 'Price on request' : 
                             i18n.language === 'zh' ? '价格面议' : 'Цена по запросу';
                    }
                    
                    if (priceM2Min && priceM2Min > 0 && priceM2Max && priceM2Max > 0 && priceM2Min !== priceM2Max) {
                      return `${formatPriceFromCurrency(priceM2Min, objectBaseCurrency)} - ${formatPriceFromCurrency(priceM2Max, objectBaseCurrency)}/м²`;
                    }
                    if (priceM2Min && priceM2Min > 0) {
                      return `${formatPriceFromCurrency(priceM2Min, objectBaseCurrency)}/м²`;
                    }
                    if (priceM2Max && priceM2Max > 0) {
                      return `${t('objectCard.upTo')} ${formatPriceFromCurrency(priceM2Max, objectBaseCurrency)}/м²`;
                    }
                    return i18n.language === 'en' ? 'Price on request' : 
                           i18n.language === 'zh' ? '价格面议' : 'Цена по запросу';
                  })()
                }} />
              </li>
            )}
          </ul>
        </Article>
      </Section>



      {/* Удобства */}
      <Section>
        <AmenitiesSection slugOrId={item.slug || item.id} />
      </Section>

      {/* Расположение */}
      <Section id="location">
        <h2 style={{margin:'0 0 16px'}}>{t('objectCard.locationSection')}</h2>
        <div style={{border:'1px solid var(--bdr)', borderRadius:16, overflow:'hidden', marginBottom: '20px'}}>
          {(() => {
            // Функция проверки валидности координат
            const isValidCoordinate = (lat, lng) => {
              const latNum = Number(lat);
              const lngNum = Number(lng);
              return !isNaN(latNum) && !isNaN(lngNum) && 
                     latNum >= -90 && latNum <= 90 && 
                     lngNum >= -180 && lngNum <= 180 &&
                     latNum !== 0 && lngNum !== 0;
            };

            if (item.lat && item.lng && isValidCoordinate(item.lat, item.lng)) {
              return (
                <YMap
                  apiKey={process.env.REACT_APP_YMAPS_KEY}
                  lang={i18n.language === 'en' ? 'en_US' : 'ru_RU'}
                  center={[item.lat, item.lng]}
                  zoom={14}
                  markers={[{
                    coords: [item.lat, item.lng],
                    title: (item.title_en && i18n.language==='en') ? item.title_en : (item.title_ru || title),
                    subtitle: item.address || '',
                  }]}
                  style={{ width: '100%', height: '360px' }}
                />
              );
            } else {
              return (
                <div style={{height:360, display:'grid', placeItems:'center', color:'var(--muted)', textAlign: 'center', padding: '20px'}}>
                  <div>
                    <div style={{fontSize: '48px', marginBottom: '10px'}}>📍</div>
                    <div style={{marginBottom: '5px'}}>
                      {t('objectCard.locationNotAvailable')}
                    </div>
                    {item.address && (
                      <div style={{fontSize: '14px', opacity: 0.7}}>
                        {item.address}
                      </div>
                    )}
                  </div>
                </div>
              );
            }
          })()}
        </div>
        
        {/* Ближайшие локации */}
        <NearbyLocations 
          locations={item.nearby_locations}
          description={item.location_description}
        />
      </Section>

      <Section><QuickConsult /></Section>

      <div style={{marginTop:12}}>
        <Link to="/search" className="btn">← {t('objectCard.backToCatalog')}</Link>
      </div>

      {open && <CallRequestModal open={open} onClose={()=>setOpen(false)} objectTitle={title} />}
    </Wrap>
  );
}
